import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ListMusic, Plus, X, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

export default function PlaylistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [playlist, setPlaylist] = useState(null);
  const [songs, setSongs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [allSongs, setAllSongs] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/playlists/${id}`);
    setPlaylist(data);
    setSongs(data.songs || []);
  };
  useEffect(() => { load(); }, [id]);

  const openAdd = async () => {
    if (!allSongs.length) {
      const { data } = await api.get('/songs');
      setAllSongs(data);
    }
    setShowAdd(true);
  };

  const addSong = async (songId) => {
    const ids = [...(playlist.song_ids || []), songId];
    await api.put(`/playlists/${id}`, { song_ids: ids });
    setShowAdd(false);
    load();
    toast.success('Chanson ajoutée');
  };

  const removeSong = async (songId) => {
    const ids = (playlist.song_ids || []).filter(s => s !== songId);
    await api.put(`/playlists/${id}`, { song_ids: ids });
    load();
  };

  const share = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/playlist/${id}`);
    toast.success('Lien copié !');
  };

  if (!playlist) return <Layout><div className="p-10 text-stone-400">Chargement...</div></Layout>;
  const owner = playlist.user_id === user?.user_id;

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-4xl">
        <Link to="/playlists" className="inline-flex items-center gap-2 text-stone-400 hover:text-stone-100 mb-6" data-testid="back-playlists">
          <ArrowLeft size={16} /> Playlists
        </Link>

        <div className="mb-8 rise">
          <div className="overline text-orange-400">{playlist.is_public ? 'public' : 'privé'} · par {playlist.user_name}</div>
          <div className="flex items-center justify-between mt-1 flex-wrap gap-3">
            <h1 className="font-display text-5xl tracking-tight">{playlist.name}</h1>
            <div className="flex items-center gap-2">
              <button data-testid="share-playlist" onClick={share} className="btn-ghost inline-flex items-center gap-2"><Share2 size={16} /> Partager</button>
              {owner && (
                <button data-testid="add-song-btn" onClick={openAdd} className="btn-primary inline-flex items-center gap-2"><Plus size={16} /> Ajouter une chanson</button>
              )}
            </div>
          </div>
          <p className="text-stone-400 mt-3 max-w-2xl">{playlist.description}</p>
        </div>

        {songs.length === 0 ? (
          <div className="card-stone p-10 text-center text-stone-400">
            <ListMusic className="mx-auto mb-3 text-stone-500" />
            Aucune chanson dans cette playlist.
          </div>
        ) : (
          <div className="space-y-3">
            {songs.map((s, i) => (
              <div key={s.id} data-testid={`playlist-song-${i}`} className="card-stone p-4 flex items-center gap-4">
                <div className="text-stone-500 w-6 font-display text-lg">{i + 1}</div>
                <div className="w-12 h-12 rounded-lg bg-stone-800 overflow-hidden shrink-0">
                  {s.cover_url && <img src={s.cover_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <Link to={`/song/${s.id}`} className="flex-1 min-w-0">
                  <div className="font-display text-lg truncate">{s.title}</div>
                  <div className="text-stone-400 text-sm truncate">{s.artist} · {s.difficulty}</div>
                </Link>
                {owner && (
                  <button data-testid={`remove-song-${i}`} onClick={() => removeSong(s.id)} className="text-stone-400 hover:text-red-400 p-2"><X size={16} /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="glass rounded-2xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="font-display text-2xl mb-4">Ajouter une chanson</h2>
            <div className="space-y-2">
              {allSongs.map(s => {
                const already = playlist.song_ids?.includes(s.id);
                return (
                  <button
                    key={s.id}
                    data-testid={`add-${s.id}`}
                    onClick={() => !already && addSong(s.id)}
                    disabled={already}
                    className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                      already ? 'border-stone-800 bg-stone-900/50 opacity-50' : 'border-stone-800 hover:border-orange-800/60'
                    }`}
                  >
                    <div className="w-10 h-10 rounded bg-stone-800 overflow-hidden shrink-0">
                      {s.cover_url && <img src={s.cover_url} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{s.title}</div>
                      <div className="text-xs text-stone-500 truncate">{s.artist}</div>
                    </div>
                    {already && <span className="text-xs text-stone-500">déjà dans la playlist</span>}
                  </button>
                );
              })}
            </div>
            <button onClick={() => setShowAdd(false)} className="btn-ghost mt-4">Fermer</button>
          </div>
        </div>
      )}
    </Layout>
  );
}
